import React, { memo, useState } from 'react';
import BEMHelper from '@ynap/bem-helper';
import { useConfig } from '@ynap/immutables-provider';
import { ProductsImageWithSkeleton } from '@ynap/image';
import Colours from '../Colours/Colours';
import { Features, FeatureSelectorProviderWrapper } from '@ynap/feature-selector';
import featuresList from 'app/features/featuresList';
import AddWishlistButton from '../AddWishlistButton/AddWishlistButton';
import { getProductImage } from '@ynap/product-state/src/selectors';

const ProductList = (props) => {
    const bem = new BEMHelper('ProductList');
    const {
        ProductSummary,
        products,
        preloadedProductItems = 12,
        productImageAspectRatio,
        imageSrcsetSize,
        productListKey,
        messages,
        loading,
        disableSPA,
        locale,
        baseUrl,
        ssr,
        wishlist,
        listingPage,
        country,
        loadingColors,
    } = props;
    const [colourImageMap, setColourImageMap] = useState({});
    const config = useConfig();
    const pdpUrlTemplate = config.global.urlConfig.pdp.construct;

    const handleColourChange = (productPartNumber, newColourPartNumber) => {
        setColourImageMap((prev) => ({
            ...prev,
            [productPartNumber]: newColourPartNumber,
        }));
    };

    if (!products) return null;

    const {
        head: { useIdentifier, minimumPriceCountries = [], plp, plpAvailableImageSizes, imageQualityPercentage, aspectRatio: { imagesphere = null } = {} } = {},
    } = config;
    const isEUcountry = minimumPriceCountries.includes(country?.toUpperCase?.());
    const showSwatches = !!plp?.colourSwatches;

    return products.map((product, i) => {
        const colourImagePartNumber = colourImageMap[product?.partNumber];
        const hoveredColorVariant = product?.productSiblings?.find((sibling) => sibling?.partNumber === colourImagePartNumber);
        const isMainColor = hoveredColorVariant?.partNumber === product?.partNumber;
        const displayedImageSrc =
            hoveredColorVariant && !isMainColor
                ? getProductImage(hoveredColorVariant, plpAvailableImageSizes, imageQualityPercentage, 0, imagesphere)
                : product?.imgSrc;

        const productWithUpdatedImage = {
            ...product,
            imgSrc: displayedImageSrc,
        };

        return (
            <div key={i} className={bem('productItemContainer')}>
                <AddWishlistButton wishlist={wishlist} product={product} locale={locale} messages={messages} />
                <ProductSummary
                    withIntersectionObserver={i > preloadedProductItems} //remove intersection observer for first 12 products
                    withSkeleton={ssr ? (i > preloadedProductItems ? true : false) : true} //remove skeleton for first 12 products on server side render only
                    className={bem('productItem')}
                    productImageAspectRatio={productImageAspectRatio}
                    key={`${productListKey}-${i}`}
                    position={i + 1}
                    product={productWithUpdatedImage}
                    messages={messages}
                    loading={loading}
                    imageSrcsetSize={imageSrcsetSize}
                    nativelink={disableSPA ? true : undefined}
                    locale={locale}
                    baseUrl={baseUrl}
                    id={`pid-${product?.partNumber}`}
                    useIdentifier={useIdentifier}
                    ImageComponent={ProductsImageWithSkeleton}
                    listingPage={listingPage}
                    isEUcountry={isEUcountry}
                    country={country}
                    addMicrodata={true}
                    colourImagePartNumber={colourImagePartNumber}
                />
                {showSwatches && (
                    <FeatureSelectorProviderWrapper features={featuresList(config)} categories={[product?.masterCategory?.identifier]}>
                        <Features>
                            <Colours
                                colours={product?.productSiblings}
                                className="colour"
                                product={product}
                                loading={loadingColors}
                                setColourImagePartNumber={(newPartNumber) => handleColourChange(product.partNumber, newPartNumber)}
                                pdpUrlTemplate={pdpUrlTemplate}
                                locale={locale}
                            />
                        </Features>
                    </FeatureSelectorProviderWrapper>
                )}
            </div>
        );
    });
};

const areEqual = (prevProps, nextProps) => {
    if (prevProps.products.length !== nextProps.products.length) {
        return false;
    }

    // badges might update when user data is fetched and segmented badges are shown
    for (let i = 0; i < prevProps.products.length; i++) {
        if (prevProps.products[i]?.badge?.key !== nextProps.products[i]?.badge?.key) {
            return false;
        }
    }

    if (prevProps.loading !== nextProps.loading) {
        return false;
    }

    if (prevProps.productListKey !== nextProps.productListKey) {
        return false;
    }
    if (prevProps.wishlist?.wishList !== nextProps.wishlist?.wishList) {
        return false;
    }

    if (prevProps.loadingColors !== nextProps.loadingColors) {
        return false;
    }

    return true;
};

export default memo(ProductList, areEqual);
